import React, { useState, useRef, useEffect } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { api } from "@/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export function AIChatFloat() {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([
    { id: "m0", role: "assistant", text: "Hi! I'm the Param ERP AI assistant. Ask me about projects, sales, cash flow or forecasts." },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  async function send() {
    if (!prompt.trim() || busy) return;
    const p = prompt.trim();
    setPrompt("");
    setMessages((m) => [...m, { id: `u_${Date.now()}`, role: "user", text: p }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant", { prompt: p, session_id: "float" });
      setMessages((m) => [...m, { id: `a_${Date.now()}`, role: "assistant", text: data.answer }]);
    } catch (e) {
      toast.error("AI assistant failed");
      setMessages((m) => [...m, { id: `a_${Date.now()}`, role: "assistant", text: "Sorry, I couldn't reach the AI service. Please try again." }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full w-14 h-14 grid place-items-center shadow-lg transition-colors"
        data-testid="ai-float-toggle"
        title="AI Business Assistant"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>

      {open && (
        <div
          data-testid="ai-float-panel"
          className="fixed bottom-24 right-6 z-50 w-[380px] max-w-[92vw] h-[520px] bg-white border border-zinc-200 rounded-lg shadow-2xl flex flex-col overflow-hidden"
        >
          <div className="px-4 py-3 bg-zinc-950 text-white flex items-center gap-2">
            <Sparkles size={16} className="text-emerald-400" />
            <div>
              <div className="text-sm font-semibold font-heading">AI Business Assistant</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400">GPT 5.6 Terra</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
            {messages.map((m, i) => (
              <div
                key={m.id || `${m.role}-${i}`}
                className={`text-sm rounded-md px-3 py-2 max-w-[85%] whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-emerald-700 text-white"
                    : "bg-white border border-zinc-200 text-zinc-800"
                }`}
              >
                {m.text}
              </div>
            ))}
            {busy && <div className="text-xs text-zinc-500 italic">Thinking…</div>}
          </div>
          <div className="p-3 border-t border-zinc-200 bg-white flex gap-2">
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about a project, lead, cash flow…"
              className="flex-1 text-sm border border-zinc-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              data-testid="ai-float-input"
            />
            <Button size="sm" onClick={send} disabled={busy} data-testid="ai-float-send">
              <Send size={14} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
